/**
 * @file: index.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * The reservation list. An always-visible status row (scrollable chips + a
 * filter toggle) sits above a collapsible filter panel (search, sort), then the
 * KPI grid, then an infinite list of reservation cards. The panel is hidden by
 * default so the list gets the room. Every control feeds the shared `filter` /
 * `sorter` driving both the list and the summary counts.
 */

import { useAIAssistantForPage } from '@euler/app/components/ai/pageContext';
import { RefreshHeaderButton } from '@euler/app/components/list/RefreshHeaderButton';
import { DefaultLoader } from '@euler/app/components/loaders/DefaultLoader';
import { FooterView } from '@euler/app/flows/home/components/FooterView';
import { LD } from '@euler/app/flows/leads/pages/detail/components/leadDetailKit';
import { ReservationsNavParams } from '@euler/app/flows/reservations';
import { CreateReservationHeaderButton } from '@euler/app/flows/reservations/pages/list/components/CreateReservationHeaderButton';
import { ReservationDrawerHeaderButton } from '@euler/app/flows/reservations/pages/list/components/ReservationDrawerHeaderButton';
import { ReservationFilterPanel } from '@euler/app/flows/reservations/pages/list/components/filter-panel';
import { ReservationKpiGrid } from '@euler/app/flows/reservations/pages/list/components/kpi';
import { ReservationCell } from '@euler/app/flows/reservations/pages/list/components/reservation-card';
import { StatusFilterRow } from '@euler/app/flows/reservations/pages/list/components/status-filter';
import { kReservationStatusTabs } from '@euler/app/flows/reservations/pages/list/functions/reservationListConstants';
import { useReservationListControls } from '@euler/app/flows/reservations/pages/list/functions/useReservationListControls';
import { useReservationStats } from '@euler/app/flows/reservations/pages/list/functions/useReservationStats';
import { useReservationsList } from '@euler/app/flows/reservations/pages/list/functions/useReservationsList';
import { useReservationCenter } from '@euler/app/flows/reservations/provider';
import { Center } from '@euler/components/Center';
import { EmptyView } from '@euler/components/EmptyView';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  useScreenContentContainerStyle,
  wrapNavigatorScreen,
} from '@euler/functions/wrapNavigatorScreen';
import {
  Reservation,
  ReservationStatus,
} from '@euler/model/reservation/Reservation';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  FlatList,
  ListRenderItem,
  Platform,
  RefreshControl,
  View,
} from 'react-native';

const keyExtractor = (item: Reservation) => item.id;

export const ReservationsListScreen = wrapNavigatorScreen(
  () => {
    const { rpx } = useResponsiveContext();
    const contentContainerStyle = useScreenContentContainerStyle();
    const navigation =
      useNavigation<StackNavigationProp<ReservationsNavParams>>();

    useAIAssistantForPage('reservations_center');

    const controls = useReservationListControls();
    const { filter, sorter, searchFilter } = controls;

    // The filter panel is collapsed by default so the list gets the room; the
    // status row stays visible and its toggle reveals the panel.
    const [filtersExpanded, setFiltersExpanded] = useState(false);
    const toggleFilters = useCallback(
      () => setFiltersExpanded(prev => !prev),
      [],
    );

    const {
      data,
      status,
      replace,
      refresh,
      fetchWithoutRefresh,
      fetchMore,
      fetchSinceLastError,
      update,
    } = useReservationsList(filter, { sorter });
    // Bumped to recompute the search-scoped counts after a detail-screen
    // edit/transition (which can move a row between buckets) — on focus-return
    // and on a live reservation-updated event.
    const [statsReloadKey, setStatsReloadKey] = useState(0);
    const stats = useReservationStats(searchFilter, statsReloadKey);

    // A user-driven reload covers both queries: the list and the KPI tiles are
    // fetched separately, so refreshing only the list would leave stale counts
    // sitting above fresh rows.
    const reloadAll = useCallback(() => {
      void refresh();
      setStatsReloadKey(k => k + 1);
    }, [refresh]);

    // The burger (opens the app drawer) on the left; the "+" (opens the walk-in
    // create-reservation screen) on the right. On web a reload button joins it —
    // the browser has no pull-to-refresh.
    useEffect(() => {
      navigation.setOptions({
        headerLeft: () => <ReservationDrawerHeaderButton />,
        headerRight: () => (
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            {Platform.OS === 'web' ? (
              <RefreshHeaderButton
                onPress={reloadAll}
                refreshing={status.isRefreshing ?? false}
              />
            ) : null}
            <CreateReservationHeaderButton />
          </View>
        ),
      });
    }, [navigation, reloadAll, status.isRefreshing]);

    // React to reservation changes without a full reload or losing scroll,
    // decoupled from nav params via the reservation-center subjects. A general
    // update patches the card in place; a cancel drops the card when the active
    // status tab no longer includes it (e.g. 待到店 → cancelled), else patches.
    // The active tab's statuses are read through a ref so the subscription
    // doesn't re-bind on every tab switch.
    const { reservationUpdated$, reservationCanceled$, reservationDeleted$ } =
      useReservationCenter();
    const filterStatusesRef = useRef<ReservationStatus[] | undefined>(
      undefined,
    );
    filterStatusesRef.current = kReservationStatusTabs.find(
      t => t.key === controls.statusKey,
    )?.status;

    useEffect(() => {
      const patch = (reservation: Reservation) => {
        update(item => {
          if (item.id === reservation.id) Object.assign(item, reservation);
          return undefined;
        });
        setStatsReloadKey(k => k + 1);
      };
      const subUpdated = reservationUpdated$.subscribe(patch);
      const subCanceled = reservationCanceled$.subscribe(reservation => {
        const statuses = filterStatusesRef.current;
        if (!statuses || statuses.includes(reservation.status)) {
          patch(reservation);
        } else {
          update(item => (item.id === reservation.id ? 'remove' : undefined));
          setStatsReloadKey(k => k + 1);
        }
      });
      const subDeleted = reservationDeleted$.subscribe(deletedId => {
        update(item => (item.id === deletedId ? 'remove' : undefined));
        setStatsReloadKey(k => k + 1);
      });
      return () => {
        subUpdated.unsubscribe();
        subCanceled.unsubscribe();
        subDeleted.unsubscribe();
      };
    }, [
      reservationUpdated$,
      reservationCanceled$,
      reservationDeleted$,
      update,
    ]);

    const onPress = useCallback(
      (reservation: Reservation) => {
        navigation.navigate('detail', { id: reservation.id });
      },
      [navigation],
    );

    const renderItem: ListRenderItem<Reservation> = useCallback(
      ({ item }) => <ReservationCell reservation={item} onPress={onPress} />,
      [onPress],
    );

    // Reload from offset 0 whenever a control changes the combined query inputs.
    // `replace` resets the data + stamps a requestId so an in-flight page load
    // can't clobber the newer result set. `sorter` is keyed by its content.
    const sorterKey = `${sorter.prop}:${sorter.dir}`;
    useEffect(() => {
      void replace();
      // `replace` is a stable persisted fn; only the query inputs retrigger it.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter, sorterKey]);

    // Reload the list + recount on focus-return from the detail / create screen
    // (a booking can be edited, transitioned, or newly created). Reload SILENTLY
    // — the RefreshControl spinner is reserved for a user pull, so a programmatic
    // focus reload must not pin the content down (which left a top gap). Skip the
    // first focus — the query-input effect above already loaded the initial page.
    const focusedOnce = useRef(false);
    useFocusEffect(
      useCallback(() => {
        if (focusedOnce.current) {
          void fetchWithoutRefresh();
          setStatsReloadKey(k => k + 1);
        } else {
          focusedOnce.current = true;
        }
        // `fetchWithoutRefresh` is a stable persisted fn.
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []),
    );

    const header = (
      <View style={{ paddingTop: rpx(12) }}>
        <StatusFilterRow
          value={controls.statusKey}
          onChange={controls.setStatusKey}
          filtersExpanded={filtersExpanded}
          onToggleFilters={toggleFilters}
        />
        {filtersExpanded ? (
          <ReservationFilterPanel controls={controls} />
        ) : null}
        <ReservationKpiGrid stats={stats} />
      </View>
    );

    return (
      <View style={{ flex: 1, backgroundColor: LD.canvas }}>
        <FlatList
          data={data}
          keyExtractor={keyExtractor}
          renderItem={renderItem}
          ListHeaderComponent={header}
          showsVerticalScrollIndicator={Platform.OS === 'web'}
          contentContainerStyle={[{ flexGrow: 1 }, contentContainerStyle]}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="on-drag"
          refreshControl={
            Platform.OS === 'web' ? undefined : (
              <RefreshControl
                refreshing={(status.isRefreshing && data.length > 0) ?? false}
                onRefresh={reloadAll}
              />
            )
          }
          onEndReachedThreshold={0.3}
          onEndReached={fetchMore}
          ListEmptyComponent={
            status.isLoading && data.length === 0 && !status.isRefreshing ? (
              <Center css={'flex: 1;'}>
                <DefaultLoader />
              </Center>
            ) : (
              <EmptyView
                error={status.error}
                onRetry={reloadAll}
                message={'暂无预约'}
              />
            )
          }
          ListFooterComponent={
            <FooterView
              isLoading={
                status.isLoading &&
                status.loadedDataLength > 0 &&
                !status.isRefreshing
              }
              total={data.length}
              error={status.error}
              hasMore={status.hasMore}
              noMoreText="没有更多预约了"
              onRetry={fetchSinceLastError}
            />
          }
        />
      </View>
    );
  },
  {
    title: '预约',
  },
);
